import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, InputLabel, LinearProgress, IconButton, Tooltip,
  MenuItem, Paper, Select, Skeleton, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Checkbox, FormControlLabel, ListItemText, OutlinedInput,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StoreIcon from '@mui/icons-material/Store';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../API/clientAPI';
import { storeAPI } from '../../API/storeAPI';
import { deviceAPI } from '../../API/deviceAPI';
import ClientAvatar from '../../Components/GatewayContagem/ClientAvatar';
import DeleteStoreModal from '../../Components/Modal/DeleteStoreModal';
import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import usePermissions from '../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../Constants/permissions';
import { buildQueryString, TABLE_HEAD_ROW_SX } from '../../Utils/format';

const normalizePlanType = (plan) => {
  if (!plan) return null;
  const p = plan.toLowerCase().trim();
  if (p === 'plataforma' || p === 'platform') return 'platform';
  if (p === 'api + plataforma' || p === 'api+plataforma' || p === 'api_platform') return 'api_platform';
  if (p === 'api') return 'api';
  return p;
};

// ── Status chip ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  operational: { color: 'success' },
  offline: { color: 'error' },
  warning: { color: 'warning' },
};

function StoreStatusChip({ statusType, offlineCount, warningCount }) {
  const { t } = useTranslation('clients');
  const cfg = STATUS_CONFIG[statusType] || STATUS_CONFIG.operational;
  const label =
    statusType === 'operational'
      ? t('status.operational')
      : statusType === 'offline'
        ? t('status.offlineCount', { count: offlineCount })
        : t('status.warningCount', { count: warningCount });
  return (
    <Chip
      size="small"
      color={cfg.color}
      label={label}
      sx={{ fontWeight: 600, minWidth: 90 }}
    />
  );
}

// ── Devices-online bar ─────────────────────────────────────────────────────────
function DevicesBar({ online, warning, offline, total }) {
  if (!total) return null;
  const pctOnline = (online / total) * 100;
  const pctWarning = (warning / total) * 100;
  const pctOffline = (offline / total) * 100;
  return (
    <Box sx={{ width: 120 }}>
      <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${pctOnline}%`, bgcolor: 'success.main' }} />
        <Box sx={{ width: `${pctWarning}%`, bgcolor: 'warning.main' }} />
        <Box sx={{ width: `${pctOffline}%`, bgcolor: 'error.main' }} />
      </Box>
    </Box>
  );
}

// ── Client accordion row ───────────────────────────────────────────────────────
function ClientSection({ client, index, setData, refreshGlobalClients, onEditClient, onEditStore, onDeleteStore, refreshKey, filters }) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const [open, setOpen] = useState(index === 0);
  const [toggling, setToggling] = useState(false);

  // Lazy loading stores and stats
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, warning: 0, offline: 0 });
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadedFilterKey, setLoadedFilterKey] = useState(null);

  const { isGatewayAdmin, isClientAdmin } = usePermissions();

  const fmtFilter = (v) => (Array.isArray(v) ? v.filter(Boolean).join(',') : (v || ''));
  const hasActiveFilters = Boolean(fmtFilter(filters?.lojaId) || fmtFilter(filters?.cidade) || fmtFilter(filters?.estado));
  const isOpen = open || hasActiveFilters;
  const currentFilterKey = `${fmtFilter(filters?.cidade)}_${fmtFilter(filters?.estado)}_${refreshKey}`;
  const detailsLoaded = Boolean(loadedFilterKey);

  const displayStores = useMemo(() => {
    let result = stores;
    if (Array.isArray(filters?.lojaId) && filters.lojaId.length > 0) {
      result = result.filter(s => filters.lojaId.includes(s.id));
    }
    return result;
  }, [stores, filters?.lojaId]);

  useEffect(() => {
    setLoadedFilterKey(null);
  }, [refreshKey]);

  useEffect(() => {
    if (isOpen && loadedFilterKey !== currentFilterKey && !loadingDetails) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        try {
          // Fetch client device stats
          const cStats = await deviceAPI.getStatsByClient(buildQueryString({ clientIds: client.id }));
          let newStats = { total: 0, online: 0, warning: 0, offline: 0 };
          if (cStats && !cStats.error && cStats.value) {
            newStats = {
              total: cStats.value.total ?? 0,
              online: cStats.value.online ?? 0,
              warning: cStats.value.warning ?? 0,
              offline: cStats.value.offline ?? 0,
            };
            setStats(newStats);
          }

          // Fetch stores using server-side filters
          const sr = await storeAPI.getByClient(client.id, {
            city: filters?.cidade,
            status: filters?.estado,
          });
          const storesRaw = Array.isArray(sr?.value) ? sr.value : [];
          const storesWithDetails = storesRaw.map((s) => {
            let statusType = 'operational';
            const devCount = s.devicesCount ?? 0;
            const offlineCount = s.devicesOfflineCount ?? 0;
            const warningCount = s.devicesWarningCount ?? 0;

            if (offlineCount > 0) {
              statusType = 'offline';
            } else if (warningCount > 0) {
              statusType = 'warning';
            }

            return {
              ...s,
              status: s.status || 'Inactive',
              area: s.area ?? 0,
              openedYear: s.openedYear ?? undefined,
              deviceCount: devCount,
              statusType,
              offlineCount,
              warningCount,
            };
          });
          setStores(storesWithDetails);

          // Update the main page client summary totals
          setData(prevData => prevData.map(c => {
            if (c.id === client.id) {
              return {
                ...c,
                devicesTotal: newStats.total,
                devicesOnline: newStats.online,
                devicesWarning: newStats.warning,
                devicesOffline: newStats.offline,
                stores: storesWithDetails,
                _detailsLoaded: true,
              };
            }
            return c;
          }));

          setLoadedFilterKey(currentFilterKey);
        } catch (err) {
          console.error('Failed to load lazy details', err);
          toast.error(t('toast.loadError', 'Erro ao carregar lojas e dispositivos'));
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchDetails();
    }
  }, [isOpen, loadedFilterKey, currentFilterKey, loadingDetails, client.id, filters?.cidade, filters?.estado, setData, t]);

  const pctOnline = stats.total
    ? Math.round((stats.online / stats.total) * 100)
    : 0;

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 2, overflow: 'hidden' }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2.5, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setOpen(o => !o)}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
        >
          <ClientAvatar name={client.name} size={36} />
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ mb: 0.2 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                lineHeight={1.2}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGatewayAdmin || isClientAdmin) {
                    navigate(`/Gateway/Clients/${client.id}`);
                  }
                }}
                sx={{
                  cursor: (isGatewayAdmin || isClientAdmin) ? 'pointer' : 'default',
                  '&:hover': (isGatewayAdmin || isClientAdmin) ? { textDecoration: 'underline', color: 'primary.main' } : {},
                }}
              >
                {client.name}
              </Typography>
              {isGatewayAdmin && (
                <Switch
                  size="small"
                  checked={client.status === 'Active'}
                  disabled={toggling}
                  onClick={e => e.stopPropagation()}
                  onChange={async (e) => {
                    e.stopPropagation();
                    const newStatus = e.target.checked ? 'Active' : 'Inactive';
                    // If activating without a plan, open the dialog instead
                    if (newStatus === 'Active' && !client.planType) {
                      onEditClient({ ...client, status: 'Active' });
                      toast(t('toast.planRequired', 'É necessário associar um plano ao cliente para o ativar.'), { icon: '⚠️' });
                      return;
                    }
                    setToggling(true);
                    try {
                      const res = await clientAPI.update(client.id, {
                        ...client,
                        status: newStatus,
                        planType: client.planType,
                      });
                      if (res && !res.error) {
                        toast.success(
                          newStatus === 'Active'
                            ? t('toast.clientActivated', 'Cliente ativado')
                            : t('toast.clientDeactivated', 'Cliente desativado')
                        );
                        setData(d => d.map((c) => (
                          c.id === client.id
                            ? { ...c, status: newStatus }
                            : c
                        )));
                        refreshGlobalClients();
                      } else {
                        toast.error(t('toast.clientError'));
                      }
                    } catch {
                      toast.error(t('toast.clientError'));
                    } finally {
                      setToggling(false);
                    }
                  }}
                  sx={{ mr: 0.5 }}
                />
              )}
              <Chip
                label={client.status === 'Active' ? t('status.active', 'Ativo') : t('status.inactive', 'Inativo')}
                size="small"
                color={client.status === 'Active' ? 'success' : 'default'}
                variant="outlined"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25 }}
              />
              {(client.planType === 'api' || client.planType === 'api_platform') && (
                <Chip
                  label="API"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25 }}
                />
              )}
              {(client.planType === 'platform' || client.planType === 'api_platform') && (
                <Chip
                  label={t('chips.platform')}
                  size="small"
                  color="primary"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, px: 0.25 }}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {t('clientHeader.summary', {
                externalId: client.externalId || '',
                stores: detailsLoaded ? stores.length : (client.storesCount || 0),
                devices: detailsLoaded ? stats.total : (client.devicesCount || 0),
              })}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          gap={3}
        >
          {detailsLoaded && stats.total > 0 && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="right"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {t('clientHeader.devicesOnline')}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
              >
                <DevicesBar
                  online={stats.online}
                  warning={stats.warning}
                  offline={stats.offline}
                  total={stats.total}
                />
                <Typography
                  variant="body2"
                  fontWeight={700}
                >
                  {pctOnline}%
                </Typography>
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={1} onClick={e => e.stopPropagation()}>
            <Tooltip title={t('actions.editClient', 'Editar Cliente')}>
              <IconButton
                size="small"
                onClick={() => onEditClient(client)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setOpen(o => !o)}
              sx={{ minWidth: 80 }}
            >
              {open ? t('clientHeader.close') : t('clientHeader.open')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {/* ── Store table ── */}
      <Collapse
        in={isOpen}
        timeout="auto"
      >
        <Divider />
        <LoadingSkeleton
          loading={loadingDetails}
          preset="table"
          count={Math.max(1, client.storesCount || 1)}
          sx={{ borderRadius: 0 }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={TABLE_HEAD_ROW_SX}>
                  <TableCell>{t('table.store')}</TableCell>
                  <TableCell>{t('table.city')}</TableCell>
                  <TableCell>{t('table.area', 'Área')}</TableCell>
                  <TableCell>{t('table.openedYear', 'Aberta em')}</TableCell>
                  <TableCell align="center">{t('table.devices')}</TableCell>
                  <TableCell>{t('table.status')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {!displayStores.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: 'text.disabled', py: 2 }}>
                      {t('empty.noStores', 'Sem lojas')}
                    </TableCell>
                  </TableRow>
                )}
                {displayStores.map(store => (
                  <TableRow
                    key={store.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/Gateway/Stores/${store.id}`, { state: { from: 'clients' } })}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.2 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                        >
                          {store.name}
                        </Typography>
                        {store.status && (
                          <Chip
                            label={store.status === 'Active' ? t('status.active', 'Ativo') : t('status.inactive', 'Inativo')}
                            size="small"
                            color={store.status === 'Active' ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ height: 16, fontSize: 9, fontWeight: 700, px: 0.25 }}
                          />
                        )}
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {store.externalId}
                      </Typography>
                    </TableCell>
                    <TableCell>{store.city}</TableCell>
                    <TableCell>{store.area ? `${store.area} m²` : '—'}</TableCell>
                    <TableCell>{store.openedYear || '—'}</TableCell>
                    <TableCell align="center">{store.deviceCount}</TableCell>
                    <TableCell>
                      <StoreStatusChip
                        statusType={store.statusType}
                        offlineCount={store.offlineCount}
                        warningCount={store.warningCount}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ pr: 1 }}
                      onClick={e => e.stopPropagation()}
                    >
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        {store.deviceCount === 0 && (
                          <Tooltip title={t('actions.deleteStore', 'Eliminar Loja')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDeleteStore(store)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('actions.editStore', 'Editar Loja')}>
                          <IconButton
                            size="small"
                            onClick={() => onEditStore(client.id, store)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/Gateway/Stores/${store.id}`, { state: { from: 'clients' } })}
                        >
                          <ChevronRightIcon fontSize="small" color="action" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </LoadingSkeleton>
      </Collapse>
    </Paper>
  );
}

// ── Empty form helpers ─────────────────────────────────────────────────────────
const EMPTY_CLIENT = { id: '', externalId: '', name: '', email: '', phone: '', address: '', city: '', postalCode: '', countryCode: '', apiKey: '', status: 'Inactive', planType: '', alertEmailEnabled: false };
const EMPTY_STORE = { id: '', externalId: '', name: '', address: '', city: '', postalCode: '', latitude: '', longitude: '', timeZone: 'Europe/Lisbon', area: '', openedYear: '', status: 'Inactive' };

// ── Main page ──────────────────────────────────────────────────────────────────
const ClientsAndStoresPage = () => {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { loadClients: refreshGlobalClients } = useGatewayContagem();
  const { can, isGatewayAdmin, isClientAdmin, user: currentUser } = usePermissions();
  const canManageStores = can(PERMISSIONS.STORES_MANAGE);

  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({ clients: 0, stores: 0, devices: 0 });
  const [loading, setLoading] = useState(true);
  const [allClientsForDropdowns, setAllClientsForDropdowns] = useState([]);

  const EMPTY_FILTERS = useMemo(() => ({ plano: [], clientId: [], lojaId: [], cidade: [], estado: [] }), []);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(val => (Array.isArray(val) ? val.length > 0 : Boolean(val))),
    [filters]
  );

  const setFilter = useCallback((key, value) => {
    const nextVal = Array.isArray(value) ? value : (value ? [value] : []);
    setFilters(f => {
      const next = { ...f, [key]: nextVal };
      if (key === 'plano') { next.clientId = []; next.lojaId = []; next.cidade = []; }
      if (key === 'clientId') { next.lojaId = []; }
      return next;
    });
  }, []);

  const loadData = useCallback(async (currentFilters, active = true) => {
    setLoading(true);
    try {
      const targetFilters = currentFilters || filters;
      const apiParams = {
        page: 1,
        itemsPerPage: 100,
      };
      if (targetFilters.plano) apiParams.planType = targetFilters.plano;
      if (targetFilters.clientId) apiParams.clientId = targetFilters.clientId;
      if (targetFilters.lojaId) apiParams.storeId = targetFilters.lojaId;
      if (targetFilters.cidade) apiParams.city = targetFilters.cidade;
      if (targetFilters.estado) apiParams.status = targetFilters.estado;

      const res = await clientAPI.list(apiParams);
      if (!active) return;
      const clients = (Array.isArray(res?.value?.items) ? res.value.items : []).map(c => ({
        ...c,
        planType: normalizePlanType(c.planType),
        stores: c.stores || [],
        storesCount: c.storesCount ?? 0,
        devicesCount: c.devicesCount ?? 0
      }));
      setData(clients);

      setAllClientsForDropdowns(prev => (prev.length === 0 && !Object.values(targetFilters).some(Boolean) ? clients : prev));
    } catch (err) {
      console.error('Failed to load data', err);
      toast.error(t('toast.loadError', 'Erro ao carregar dados'));
    } finally {
      if (active) setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    let active = true;
    loadData(filters, active);
    return () => { active = false; };
  }, [filters, loadData]);

  const [clientDialog, setClientDialog] = useState(false);
  const [storeDialog, setStoreDialog] = useState(false);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [storeForm, setStoreForm] = useState(EMPTY_STORE);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [saving, setSaving] = useState(false);
  const [storeRefreshMap, setStoreRefreshMap] = useState({});
  const [activateStoresConfirmOpen, setActivateStoresConfirmOpen] = useState(false);

  const [deleteStoreTarget, setDeleteStoreTarget] = useState(null);
  const [isDeletingStore, setIsDeletingStore] = useState(false);

  const handleConfirmDeleteStore = async () => {
    if (!deleteStoreTarget) return;
    setIsDeletingStore(true);
    try {
      const res = await storeAPI.remove(deleteStoreTarget.id);
      if (res !== undefined && res !== null && !res.error) {
        toast.success(t('toast.storeDeleted', 'Loja eliminada com sucesso'));
        const { clientId } = deleteStoreTarget;
        if (clientId) {
          setStoreRefreshMap(m => ({ ...m, [clientId]: (m[clientId] ?? 0) + 1 }));
        }
        setDeleteStoreTarget(null);
        loadData(filters, true);
      } else {
        toast.error(res?.description || t('toast.storeDeleteError', 'Erro ao eliminar loja'));
      }
    } catch (err) {
      toast.error(t('toast.storeDeleteError', 'Erro ao eliminar loja'));
    } finally {
      setIsDeletingStore(false);
    }
  };

  const [selectableDevices, setSelectableDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!storeDialog) {
      setSelectableDevices([]);
      setSelectedDeviceIds([]);
      return;
    }

    const load = async () => {
      if (!selectedClientId) return;
      setDevicesLoading(true);
      try {
        let currentDevices = [];
        if (storeForm.id && storeForm.id !== 'new') {
          const resCurrent = await deviceAPI.list(buildQueryString({
            clientIds: selectedClientId,
            storeId: storeForm.id,
            itemsPerPage: 100,
          }));
          if (resCurrent && !resCurrent.error && active) {
            currentDevices = resCurrent.value?.items || [];
          }
        }

        const resUnmanaged = await deviceAPI.list(buildQueryString({
          clientIds: selectedClientId,
          isUnmanaged: true,
          itemsPerPage: 100,
        }));

        let unmanagedDevices = [];
        if (resUnmanaged && !resUnmanaged.error && active) {
          unmanagedDevices = resUnmanaged.value?.items || [];
        }

        if (!active) return;

        // Filter out duplicates if any
        const currentIds = new Set(currentDevices.map((d) => d.id));
        const filteredUnmanaged = unmanagedDevices.filter((d) => !currentIds.has(d.id));

        const allDevices = [...currentDevices, ...filteredUnmanaged];
        setSelectableDevices(allDevices);
        setSelectedDeviceIds(currentDevices.map((d) => d.id));
      } catch (err) {
        console.error('Error loading selectable devices:', err);
        if (active) toast.error(t('toast.devicesError', 'Erro ao carregar dispositivos'));
      } finally {
        if (active) setDevicesLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [storeDialog, storeForm.id, selectedClientId, t]);

  const dropdownBaseClients = allClientsForDropdowns.length ? allClientsForDropdowns : data;

  // Filter lists based on roles
  const filteredClientsForDropdown = useMemo(() => {
    if (isClientAdmin) {
      return dropdownBaseClients.filter(c => c.id === currentUser.clientId);
    }
    return dropdownBaseClients;
  }, [dropdownBaseClients, isClientAdmin, currentUser?.clientId]);

  // Options for cascading filter dropdowns
  const clientOptions = useMemo(
    () =>
      filteredClientsForDropdown
        .filter(c => !filters.plano?.length || filters.plano.includes(c.planType))
        .map(c => ({ id: c.id, name: c.name })),
    [filteredClientsForDropdown, filters.plano]
  );

  const [lojaOptions, setLojaOptions] = useState([]);
  const [cidadeOptions, setCidadeOptions] = useState([]);

  const selectedClientIdsJoined = useMemo(() => {
    if (isClientAdmin) return currentUser?.clientId || '';
    return Array.isArray(filters.clientId) ? filters.clientId.join(',') : (filters.clientId || '');
  }, [filters.clientId, isClientAdmin, currentUser?.clientId]);

  // Fetch store options when clientId filter changes
  useEffect(() => {
    if (!selectedClientIdsJoined) {
      setLojaOptions([]);
      return;
    }
    let active = true;
    storeAPI.getByClient(selectedClientIdsJoined).then(res => {
      if (active && res && !res.error && Array.isArray(res.value)) {
        setLojaOptions(res.value.map(s => ({ id: s.id, name: s.name })));
      }
    });
    return () => { active = false; };
  }, [selectedClientIdsJoined]);

  // Fetch distinct city options across stores
  useEffect(() => {
    let active = true;
    const params = { itemsPerPage: 500 };
    if (selectedClientIdsJoined) params.clientId = selectedClientIdsJoined;

    storeAPI.list(params).then(res => {
      if (active && res && !res.error && Array.isArray(res.value?.items)) {
        const cities = [...new Set(res.value.items.map(s => s.city).filter(Boolean))].sort();
        setCidadeOptions(cities);
      }
    });
    return () => { active = false; };
  }, [selectedClientIdsJoined]);

  // Server-side filtered accordion data
  const filteredData = useMemo(() => {
    if (isClientAdmin) {
      return data.filter(c => c.id === currentUser.clientId);
    }
    return data;
  }, [data, isClientAdmin, currentUser?.clientId]);

  // Recalculate totals whenever data changes
  useEffect(() => {
    setTotals(
      filteredClientsForDropdown.reduce(
        (acc, c) => ({
          clients: acc.clients + 1,
          stores: acc.stores + (c.storesCount || 0),
          devices: acc.devices + (c.devicesCount || 0)
        }),
        { clients: 0, stores: 0, devices: 0 }
      )
    );
  }, [filteredClientsForDropdown]);

  // Edit actions triggered from ClientSection
  const handleEditClientOpen = (client) => {
    navigate(`/Gateway/Clients/${client.id}`, { state: { edit: true } });
  };

  const handleEditStoreOpen = (clientId, store) => {
    setSelectedClientId(clientId);
    setStoreForm({
      id: store.id,
      externalId: store.externalId ?? '',
      name: store.name ?? '',
      address: store.address ?? '',
      city: store.city ?? '',
      postalCode: store.postalCode ?? '',
      latitude: store.latitude ?? '',
      longitude: store.longitude ?? '',
      timeZone: store.timeZone ?? 'Europe/Lisbon',
      area: store.area ?? '',
      openedYear: store.openedYear ?? '',
      status: store.status ?? 'Inactive',
    });
    setStoreDialog(true);
  };

  const executeSaveClient = async (activateAllStores) => {
    try {
      const payload = isGatewayAdmin
        ? clientForm
        : {
            // Client admins cannot modify planType, status, isActive or externalId
            ...clientForm,
            planType: data.find(c => c.id === clientForm.id)?.planType,
            status: data.find(c => c.id === clientForm.id)?.status,
            isActive: data.find(c => c.id === clientForm.id)?.isActive,
            externalId: data.find(c => c.id === clientForm.id)?.externalId,
          };

      const res = await clientAPI.update(clientForm.id, payload);
      if (res && !res.error) {
        const savedClient = {
          ...payload,
          planType: normalizePlanType(payload.planType)
        };

        if (activateAllStores && isGatewayAdmin) {
          const clientData = data.find(c => c.id === clientForm.id);
          const storesToActivate = clientData?.stores || [];

          await Promise.all(
            storesToActivate.map(async (store) => {
              if (store.status !== 'Active') {
                const spayload = {
                  name: store.name,
                  address: store.address,
                  city: store.city,
                  postalCode: store.postalCode,
                  timeZone: store.timeZone,
                  isActive: store.isActive !== false,
                  status: 'Active',
                  latitude: parseFloat(store.latitude) || 0,
                  longitude: parseFloat(store.longitude) || 0,
                  area: store.area,
                  openedYear: store.openedYear,
                };
                await storeAPI.update(store.id, spayload);
              }
            })
          );

          toast.success(t('toast.clientUpdated', 'Cliente atualizado com sucesso'));

          setData(d => d.map(c => {
            if (c.id === clientForm.id) {
              return {
                ...c,
                ...savedClient,
                stores: (c.stores || []).map(s => ({
                  ...s,
                  status: 'Active'
                }))
              };
            }
            return c;
          }));
        } else {
          toast.success(t('toast.clientUpdated', 'Cliente atualizado com sucesso'));
          setData(d => d.map(c => (c.id === clientForm.id ? { ...c, ...savedClient } : c)));
        }

        setClientForm(EMPTY_CLIENT);
        setClientDialog(false);
        refreshGlobalClients();
      } else {
        toast.error(t('toast.clientError'));
      }
    } catch (err) {
      console.error('Failed to save client', err);
      toast.error(t('toast.clientError'));
    }
  };

  const handleSaveClient = async () => {
    if (saving || activateStoresConfirmOpen) return;
    if (!clientForm.id) { toast.error(t('toast.clientRequired', 'Selecione um cliente')); return; }
    if (isGatewayAdmin && clientForm.status === 'Active' && (!clientForm.planType || clientForm.planType === '')) {
      toast.error(t('toast.planRequiredForStatus', 'O plano é obrigatório para o estado selecionado'));
      return;
    }
    if (clientForm.alertEmailEnabled && !clientForm.email) {
      toast.error(t('toast.emailRequiredForAlerts'));
      return;
    }

    const clientData = data.find(c => c.id === clientForm.id);
    const isActivating = clientForm.status === 'Active' && clientData?.status !== 'Active';
    const hasStores = clientData?.stores?.length > 0;

    if (isGatewayAdmin && isActivating && hasStores) {
      setActivateStoresConfirmOpen(true);
      return;
    }

    setSaving(true);
    try {
      await executeSaveClient(false);
    } finally {
      setSaving(false);
    }
  };

  // ── Save store configuration ──
  const handleSaveStore = async () => {
    const targetClientId = isClientAdmin ? currentUser.clientId : selectedClientId;
    if (!targetClientId) { toast.error(t('toast.clientRequired', 'Selecione um cliente')); return; }
    if (!storeForm.id) { toast.error(t('toast.storeRequired', 'Selecione uma loja')); return; }
    setSaving(true);
    try {
      const payload = {
        name: storeForm.name,
        externalId: storeForm.externalId,
        address: storeForm.address,
        city: storeForm.city,
        postalCode: storeForm.postalCode,
        latitude: parseFloat(storeForm.latitude) || 0,
        longitude: parseFloat(storeForm.longitude) || 0,
        area: (storeForm.area !== '' && storeForm.area !== null && storeForm.area !== undefined && !isNaN(storeForm.area)) ? parseInt(storeForm.area, 10) : null,
        openedYear: (storeForm.openedYear !== '' && storeForm.openedYear !== null && storeForm.openedYear !== undefined && !isNaN(storeForm.openedYear)) ? parseInt(storeForm.openedYear, 10) : null,
        timeZone: storeForm.timeZone || 'Europe/Lisbon',
        status: storeForm.status || 'Active',
        clientId: targetClientId,
      };

      if (storeForm.id === 'new') {
        if (!storeForm.name.trim()) {
          toast.error(t('toast.nameRequired', 'Nome obrigatório'));
          setSaving(false);
          return;
        }
        const res = await storeAPI.create(payload);
        if (res && !res.error) {
          const newStoreId = res.value?.id || res.data?.value?.id;
          if (newStoreId && selectedDeviceIds.length > 0) {
            const assocRes = await deviceAPI.associate(newStoreId, selectedDeviceIds);
            if (assocRes && assocRes.error) {
              toast.error(assocRes.description || t('toast.storeError', 'Erro ao associar dispositivos'));
              return;
            }
          }
          toast.success(t('toast.storeCreated', 'Loja criada com sucesso'));
          setStoreRefreshMap(m => ({ ...m, [targetClientId]: (m[targetClientId] ?? 0) + 1 }));
          loadData();
          setStoreForm(EMPTY_STORE);
          setSelectedClientId('');
          setStoreDialog(false);
          refreshGlobalClients();
        } else {
          toast.error(t('toast.storeError', 'Erro ao criar loja'));
        }
      } else {
        const res = await storeAPI.update(storeForm.id, payload);
        if (res && !res.error) {
          const assocRes = await deviceAPI.associate(storeForm.id, selectedDeviceIds);
          if (assocRes && assocRes.error) {
            toast.error(assocRes.description || t('toast.storeError', 'Erro ao associar dispositivos'));
            return;
          }
          toast.success(t('toast.storeUpdated', 'Loja atualizada com sucesso'));
          setStoreRefreshMap(m => ({ ...m, [targetClientId]: (m[targetClientId] ?? 0) + 1 }));
          loadData();
          setStoreForm(EMPTY_STORE);
          setSelectedClientId('');
          setStoreDialog(false);
          refreshGlobalClients();
        } else {
          toast.error(t('toast.storeError', 'Erro ao atualizar loja'));
        }
      }
    } catch (err) {
      console.error('Failed to save store', err);
      toast.error(t('toast.storeError', 'Erro ao processar loja'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, form, setForm, type = 'text', required = false, disabled = false) => (
    <TextField
      key={key}
      label={`${label}${required ? ' *' : ` (${t('fields.optional', 'opcional')})`}`}
      value={form[key] ?? ''}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      size="small"
      fullWidth
      type={type}
      required={required}
      disabled={disabled}
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1200 }} />}
      {/* ── Page header ── */}
      <PageHeader
        title={t('title')}
        subtitle={
          loading ? (
            <Skeleton variant="text" width={220} height={20} animation="wave" />
          ) : (
            t('subtitle', { clients: totals.clients, stores: totals.stores, devices: totals.devices })
          )
        }
      >
        {canManageStores && (
          <Button
            variant="contained"
            startIcon={<StoreIcon />}
            onClick={() => {
              setStoreForm({ ...EMPTY_STORE, id: 'new' });
              setSelectedClientId(isClientAdmin ? currentUser.clientId : '');
              setStoreDialog(true);
            }}
          >
            {t('actions.createStore', 'Criar Loja')}
          </Button>
        )}
      </PageHeader>

      {/* ── Filters ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3, alignItems: 'center' }}>
        {!isClientAdmin && (
          <FormControl
            size="small"
            sx={{ minWidth: 160 }}
          >
            <InputLabel>{t('filters.plan')}</InputLabel>
            <Select
              multiple
              value={filters.plano}
              onChange={e => setFilter('plano', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label={t('filters.plan')} />}
              renderValue={(selected) => {
                if (selected.length === 0) return t('filters.all');
                const planLabels = {
                  api: t('filters.apiOnly'),
                  api_platform: t('filters.apiPlatform'),
                  platform: t('filters.platformOnly', 'Apenas Plataforma'),
                };
                return selected.map(val => planLabels[val] || val).join(', ');
              }}
            >
              {[
                { value: 'api', label: t('filters.apiOnly') },
                { value: 'api_platform', label: t('filters.apiPlatform') },
                { value: 'platform', label: t('filters.platformOnly', 'Apenas Plataforma') }
              ].map(p => (
                <MenuItem key={p.value} value={p.value}>
                  <Checkbox checked={filters.plano.indexOf(p.value) > -1} />
                  <ListItemText primary={p.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isClientAdmin && (
          <FormControl
            size="small"
            sx={{ minWidth: 180 }}
          >
            <InputLabel>{t('filters.client')}</InputLabel>
            <Select
              multiple
              value={filters.clientId}
              onChange={e => setFilter('clientId', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label={t('filters.client')} />}
              renderValue={(selected) => {
                if (selected.length === 0) return t('filters.all');
                return clientOptions
                  .filter(c => selected.includes(c.id))
                  .map(c => c.name)
                  .join(', ');
              }}
            >
              {clientOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Checkbox checked={filters.clientId.indexOf(c.id) > -1} />
                  <ListItemText primary={c.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl
          size="small"
          sx={{ minWidth: 180 }}
          disabled={!isClientAdmin && filters.clientId.length === 0}
        >
          <InputLabel>{t('filters.store')}</InputLabel>
          <Select
            multiple
            value={filters.lojaId}
            onChange={e => setFilter('lojaId', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label={t('filters.store')} />}
            renderValue={(selected) => {
              if (selected.length === 0) return t('filters.allFem');
              return lojaOptions
                .filter(s => selected.includes(s.id))
                .map(s => s.name)
                .join(', ');
            }}
          >
            {lojaOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                <Checkbox checked={filters.lojaId.indexOf(s.id) > -1} />
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 150 }}
        >
          <InputLabel>{t('filters.city')}</InputLabel>
          <Select
            multiple
            value={filters.cidade}
            onChange={e => setFilter('cidade', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label={t('filters.city')} />}
            renderValue={(selected) => (selected.length === 0 ? t('filters.allFem') : selected.join(', '))}
          >
            {cidadeOptions.map(c => (
              <MenuItem key={c} value={c}>
                <Checkbox checked={filters.cidade.indexOf(c) > -1} />
                <ListItemText primary={c} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 160 }}
        >
          <InputLabel>{t('filters.status')}</InputLabel>
          <Select
            multiple
            value={filters.estado}
            onChange={e => setFilter('estado', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label={t('filters.status')} />}
            renderValue={(selected) => {
              if (selected.length === 0) return t('filters.all');
              return selected.map(s => t(`status.${s}`, s)).join(', ');
            }}
          >
            {[
              { value: 'operational', label: t('status.operational') },
              { value: 'offline', label: t('status.offline') },
              { value: 'warning', label: t('status.warning') }
            ].map(st => (
              <MenuItem key={st.value} value={st.value}>
                <Checkbox checked={filters.estado.indexOf(st.value) > -1} />
                <ListItemText primary={st.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button
            size="small"
            color="inherit"
            onClick={() => setFilters(EMPTY_FILTERS)}
            sx={{ color: 'text.secondary', textDecoration: 'underline', textTransform: 'none', minWidth: 0 }}
          >
            {t('actions.clearFilters')}
          </Button>
        )}
      </Box>

      {/* ── Client accordion list ── */}
      {loading && !filteredData.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={64} animation="wave" sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : (
        <>
          {filteredData.map((client, idx) => (
            <ClientSection
              key={client.id}
              client={client}
              index={idx}
              setData={setData}
              refreshGlobalClients={refreshGlobalClients}
              onEditClient={handleEditClientOpen}
              onEditStore={handleEditStoreOpen}
              onDeleteStore={setDeleteStoreTarget}
              refreshKey={storeRefreshMap[client.id] ?? 0}
              filters={filters}
            />
          ))}

          {!filteredData.length && (
            <Paper
              variant="outlined"
              sx={{ p: 4, textAlign: 'center' }}
            >
              <Typography color="text.secondary">
                {hasActiveFilters ? t('empty.filtered') : t('empty.noClients')}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* ── Edit Client dialog ── */}
      <Dialog
        open={clientDialog}
        onClose={() => setClientDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('clientDialog.editTitle', 'Editar Cliente')}</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            mt={1}
          >
            {field('name', t('clientDialog.fields.name', 'Nome'), clientForm, setClientForm, 'text', true)}
            {field('externalId', t('clientDialog.fields.externalId', 'ID externo'), clientForm, setClientForm, 'text', false, !isGatewayAdmin)}

            {isGatewayAdmin && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={0.5}
                  display="block"
                >
                  {t('filters.status', 'Estado')} *
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={clientForm.status || ''}
                  displayEmpty
                  onChange={e => setClientForm(f => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="" disabled>
                    {t('storeDialog.statusPlaceholder', 'Seleccione o estado')}
                  </MenuItem>
                  <MenuItem value="Active">{t('status.active', 'Ativo')}</MenuItem>
                  <MenuItem value="Inactive">{t('status.inactive', 'Inativo')}</MenuItem>
                </Select>
              </Box>
            )}

            {isGatewayAdmin && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={0.5}
                  display="block"
                >
                  {t('filters.plan', 'Plano')} {clientForm.status === 'Active' ? '*' : '(opcional)'}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={clientForm.planType || ''}
                  displayEmpty
                  onChange={e => setClientForm(f => ({ ...f, planType: e.target.value }))}
                >
                  <MenuItem value="">
                    <em>{t('storeDialog.planPlaceholder', 'Seleccione o plano')}</em>
                  </MenuItem>
                  <MenuItem value="api">API</MenuItem>
                  <MenuItem value="api_platform">{t('filters.apiPlatform', 'API + Plataforma')}</MenuItem>
                  <MenuItem value="platform">{t('chips.platform', 'Plataforma')}</MenuItem>
                </Select>
              </Box>
            )}

            {field('email', t('clientDialog.fields.email'), clientForm, setClientForm, 'email')}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                py: 1,
              }}
            >
              <Box>
                <Typography variant="body2">{t('clientDialog.fields.alertEmailEnabled')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('clientDialog.fields.alertEmailEnabledHint')}
                </Typography>
              </Box>
              <Switch
                size="small"
                checked={!!clientForm.alertEmailEnabled}
                onChange={e => setClientForm(f => ({ ...f, alertEmailEnabled: e.target.checked }))}
              />
            </Box>

            {field('phone', t('clientDialog.fields.phone'), clientForm, setClientForm)}
            {field('address', t('clientDialog.fields.address'), clientForm, setClientForm)}
            {field('city', t('clientDialog.fields.city'), clientForm, setClientForm)}
            {field('postalCode', t('clientDialog.fields.postalCode'), clientForm, setClientForm)}
            {field('countryCode', t('clientDialog.fields.countryCode', 'País (ISO)'), clientForm, setClientForm)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDialog(false)}>{t('actions.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveClient}
            disabled={saving || activateStoresConfirmOpen}
          >
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Store dialog (Create/Edit) ── */}
      <Dialog
        open={storeDialog}
        onClose={() => setStoreDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{storeForm.id === 'new' ? t('storeDialog.createTitle', 'Criar Loja') : t('storeDialog.configureTitle', 'Configurar Loja')}</DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            mt={1}
          >
            {!isClientAdmin && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  mb={0.5}
                  display="block"
                >
                  {t('storeDialog.clientLabel')}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={selectedClientId}
                  onChange={e => {
                    setSelectedClientId(e.target.value);
                  }}
                  disabled={storeForm.id !== 'new'}
                  displayEmpty
                >
                  <MenuItem
                    value=""
                    disabled
                  >{t('storeDialog.clientPlaceholder')}</MenuItem>
                  {data.map(c => (
                    <MenuItem
                      key={c.id}
                      value={c.id}
                    >{c.name}</MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {storeForm.id === 'new' ? (
              field('name', t('storeDialog.fields.name'), storeForm, setStoreForm, 'text', true)
            ) : (
              field('name', t('storeDialog.fields.name'), storeForm, setStoreForm, 'text', true)
            )}

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                mb={0.5}
                display="block"
              >
                {t('filters.status', 'Estado')} *
              </Typography>
              <Select
                fullWidth
                size="small"
                value={storeForm.status || ''}
                displayEmpty
                onChange={e => setStoreForm(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="" disabled>
                  {t('storeDialog.statusPlaceholder', 'Seleccione o estado')}
                </MenuItem>
                <MenuItem value="Active">{t('status.active', 'Ativo')}</MenuItem>
                <MenuItem value="Inactive">{t('status.inactive', 'Inativo')}</MenuItem>
              </Select>
            </Box>

            {field('address', t('storeDialog.fields.address'), storeForm, setStoreForm, 'text', false)}
            {field('city', t('storeDialog.fields.city'), storeForm, setStoreForm, 'text', false)}
            {field('postalCode', t('storeDialog.fields.postalCode'), storeForm, setStoreForm, 'text', false)}
            {field('timeZone', t('storeDialog.fields.timeZone'), storeForm, setStoreForm, 'text', false)}
            {field('area', t('storeDialog.fields.area', 'Área (m²)'), storeForm, setStoreForm, 'number', false)}
            {field('openedYear', t('storeDialog.fields.openedYear', 'Ano de abertura'), storeForm, setStoreForm, 'number', false)}

            <Divider sx={{ my: 1 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                {t('storeDialog.associatedDevices', 'Dispositivos Associados')}
              </Typography>
              {!devicesLoading && selectableDevices.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectableDevices.length > 0 && selectedDeviceIds.length === selectableDevices.length}
                      indeterminate={selectedDeviceIds.length > 0 && selectedDeviceIds.length < selectableDevices.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDeviceIds(selectableDevices.map(d => d.id));
                        } else {
                          setSelectedDeviceIds([]);
                        }
                      }}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: 12, fontWeight: 500 }}>{t('storeDialog.selectAll', 'Selecionar Todos')}</Typography>}
                  sx={{ mr: 0 }}
                />
              )}
            </Stack>
            {devicesLoading ? (
              <LinearProgress sx={{ my: 1 }} />
            ) : selectableDevices.length > 0 ? (
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '4px', display: 'flex', flexDirection: 'column' }}>
                {selectableDevices.map((device) => {
                  const isChecked = selectedDeviceIds.includes(device.id);
                  return (
                    <Box
                      key={device.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.5,
                        px: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDeviceIds((ids) => [...ids, device.id]);
                          } else {
                            setSelectedDeviceIds((ids) => ids.filter((id) => id !== device.id));
                          }
                        }}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {device.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', fontFamily: 'monospace' }}>
                          {device.macAddress || device.externalId}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                {t('storeDialog.noDevicesAvailable', 'Nenhum dispositivo disponível para associação')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStoreDialog(false)}>{t('actions.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveStore}
            disabled={saving}
          >
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Activate Stores Confirmation dialog ── */}
      <Dialog
        open={activateStoresConfirmOpen}
        onClose={() => setActivateStoresConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('activateStoresDialog.title', 'Ativar Lojas')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('activateStoresDialog.message', 'Deseja também alterar o estado de todas as lojas deste cliente para Ativo?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateStoresConfirmOpen(false)} color="inherit">
            {t('actions.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={async () => {
              setActivateStoresConfirmOpen(false);
              setSaving(true);
              try {
                await executeSaveClient(false);
              } finally {
                setSaving(false);
              }
            }}
            color="primary"
          >
            {t('activateStoresDialog.no', 'Não')}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setActivateStoresConfirmOpen(false);
              setSaving(true);
              try {
                await executeSaveClient(true);
              } finally {
                setSaving(false);
              }
            }}
            color="primary"
          >
            {t('activateStoresDialog.yes', 'Sim')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Store Confirmation Modal ── */}
      <DeleteStoreModal
        open={Boolean(deleteStoreTarget)}
        store={deleteStoreTarget}
        isSubmitting={isDeletingStore}
        onClose={() => setDeleteStoreTarget(null)}
        onConfirm={handleConfirmDeleteStore}
      />
    </Box>
  );
};

export default ClientsAndStoresPage;
